import cryptico from 'cryptico';
import PasswordManager from './PasswordManager.js';
import ContentKey from './ContentKey.js';
import {splitIntoBytes32} from './splitIntoBytes32.js';
import aesjs from 'aes-js';
import ipfs from 'decentralized/Ipfs.js';

const BITS = 1024;

/**
 * Responsible for encrypting/decrypting content such as user sequences
 * and presets. Interfaces with Encryption.js which in turn interfaces
 * with the smart contract
 * Handles stuff like: content keys, master keys, encryption/decryption
 */
class Crypto {
    constructor(ethereum, store) {
        this.ethereum = ethereum;
        this.web3 = this.ethereum.web3;
        this.passwordManager = new PasswordManager(store);
        this.store = store;
    }

    set store(store) {
        this.passwordManager.store = store;
    }

    needsPassword() {
        return this.passwordManager.needsPassword();
    }

    set password(password) {
        this.passwordManager.password = password;
    }

    // syncs the public key to ethereum
    async syncPassword() {
        this.passwordManager.syncPassword();
    }

    async isCorrectPassword(password) {
        let ret = await this.passwordManager.isCorrectPassword(password);
        return ret;
    }

    async getPassword() {
        return await this.passwordManager.getPassword();
    }

    get masterPublicKey() {
        return this.passwordManager.masterPublicKey;
    }

    get masterKey() {
        return this.passwordManager.masterKey;
    }

    set masterPublicKey(masterPublicKey) {
        this.passwordManager.masterPublicKey = masterPublicKey;
    }

    parseGuildKey(obj) {
        let {privateKey} = obj;
        var rsa = cryptico.RSAKey.parse(JSON.stringify(privateKey));
        return {
            ... obj,
            privateKey: rsa
        };
    }

    generateGuildKey(passphrase) {
        let guildPrivateKey = cryptico.generateRSAKey(passphrase, BITS);
        let guildPublicKey = cryptico.publicKeyString(guildPrivateKey);
        let convertedPublicKey = this.ethereum.web3.utils.toHex(guildPublicKey);

        return {
            publicKey: splitIntoBytes32(convertedPublicKey),
            rawPublicKey: guildPublicKey, 
            privateKey: guildPrivateKey,
            passphrase: passphrase,
        };
    }

    encryptAndSave(name, json, previousSequence, saveToEthereum=true) {
        return new Promise(async (resolve, reject) => {
            let publicKey = await this.passwordManager.getPublicKeyIfNeeded(
                this.masterPublicKey);
            var contentKey = new ContentKey();
            contentKey.encryptKey(this.masterPublicKey);

            if (previousSequence) {
                // use the same content key as the previous sequence so that
                // others with permission to view the old sequence can still
                // view this one
                let encryptedSequence = await this.ethereum.encryption
                    .getEncryptedSequenceIfAny(previousSequence);
                if (encryptedSequence !== undefined) {
                    let _contentKey = encryptedSequence[0].contentKey;
                    if (_contentKey) {
                        contentKey = new ContentKey(aesjs.utils.hex.toBytes(contentKey));
                    }
                }
            }
            // encrypt the json 
            let encryptedContent = await contentKey
                .encryptContent(JSON.stringify(json));

            // add the json to ipfs and then ethereum (via Encryption contract)
            ipfs.addJson(encryptedContent)
                .then(async hash => {
                    this.ethereum.encryption.newEncryptedSequence(
                        previousSequence,
                        hash,
                        await contentKey.encryptContent(name),
                        contentKey.convertForEthereum(),
                        this.passwordManager.getPublicKeyAsBytes32());
                    resolve(hash);
                });
        });
    }

    // returns the encrypted data required
    async encryptRawContent(
        content,
        name,
        publicKey=this.masterPublicKey,
        isBytes=false,
        contentKey)
    {
        publicKey = await this.passwordManager.getPublicKeyIfNeeded(publicKey);
        if (contentKey === undefined) {
            contentKey = new ContentKey();
        }
        contentKey.encryptKey(publicKey);
        return {
            publicKey: this.passwordManager.getPublicKeyAsBytes32(publicKey),
            encryptedContentKey: contentKey.convertForEthereum(),
            contentKey: contentKey,
            encryptedContent: await contentKey.encryptContent(content, isBytes),
            encryptedName: await contentKey.encryptContent(name),
            eCk: contentKey.encryptedContentKey,
        };
    }

    encryptAndUploadJSON(content, masterPublicKey=this.masterPublicKey) {
        return new Promise(async (resolve, reject) => {
            let ret = await this.encryptRawContent(
                JSON.stringify(content),
                '',
                masterPublicKey);
            ipfs.addJson(ret.encryptedContent).then(
                hash => resolve({
                    publicKey: ret.publicKey,
                    encryptedContentKey: ret.encryptedContentKey,
                    encryptedContentIpfsHash: hash,
                    contentKey: ret.contentKey
                }));
        });
    }

    async decryptName(name, encryptedContentKey, masterKey) {
        if (name === null || name === undefined) {
            return '';
        }
        masterKey = await this.passwordManager
            .getPrivateKeyIfNeeded(masterKey);

        var encryptedBytes = aesjs.utils.hex.toBytes(name);
        let contentKey = await this.decryptContentKey(
            encryptedContentKey, masterKey);
        if (contentKey === undefined) {
            return '';
        }
        name = await contentKey.decryptContent(name);
        return name;
    }

    async decryptContentKey(encryptedContentKey, masterKey) {
        masterKey = await this.passwordManager
            .getPrivateKeyIfNeeded(masterKey);
        let b64Encrypted = this.web3.utils.toAscii('0x' + encryptedContentKey);
        let b64 = cryptico.decrypt(b64Encrypted, masterKey);
        if (b64.status === 'failure') {
            return undefined;
        }
        return new ContentKey(b64.plaintext);

    }

    async fetchAndDecrypt(hash, encrypted, isBytes=false, sleepTime) {
        let dateA = new Date();
        if (encrypted.length !== undefined) {
            encrypted = encrypted[0];
        }
        let decryptedContentKeyHex  = encrypted.contentKey;
        let encryptedContent = isBytes ? await ipfs.getSound(hash) : await ipfs.getJson(hash, undefined, sleepTime, true);
        await this.passwordManager.getPrivateKeyIfNeeded();
        let contentKey = new ContentKey(decryptedContentKeyHex);

        let decrypted = await contentKey.decryptContent(encryptedContent, isBytes);
        return decrypted;
    }

    stripHexPrefix(str) {
        if (str.indexOf('0x') === 0) {
            return str.slice(2);
        } else {
            return str;
        }
    }

    strip(str, len) {
        return str.slice(0, str.length - len);
    }

    getPublicKeyFromBytes32(list) {
        let total = '0x';
        for (let i in list) {
            total += this.stripHexPrefix(list[i]);
        }

        total = this.strip(total, 38);
        return this.ethereum.web3.utils.toAscii(total);
    }

    shareSequence(sequence, userId) {
        return new Promise(async (resolve, reject) => {
            // get public key of userId we want to share with
            let publicKey = await this.ethereum.encryption.getPublicKey(userId);
            if (publicKey === undefined) {
                reject("User has no public key");
                return;
            }
            
            // encrypt content key using that
            let encryptedContentKey = cryptico.encrypt(
                sequence.contentKey, publicKey)
                .cipher; 
            encryptedContentKey = this.web3.utils.toHex(encryptedContentKey);
            
            this.ethereum.encryption.shareEncryptedContent(
                userId,
                sequence.id,
                splitIntoBytes32(encryptedContentKey),
                this.passwordManager.getPublicKeyAsBytes32());

            resolve();
        });
    }
}

export default Crypto;
